import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppSettingsSidebar } from "@/components/app-settings-sidebar";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemeSettings } from "@/components/ThemeSettings";
import { useTheme } from "@/contexts/ThemeContext";
import { SETTINGS_TABS } from "@/config/settingsTabs";
import { getUserInfo } from "@/services/userService";

import BillingSection from "./sections/BillingSection";
import CompanySection from "./sections/CompanySection";
import GeneralSection from "./sections/GeneralSection";
import AccountSection from "./sections/AccountSection";
import FeaturesSection from "./sections/FeaturesSection";
import IntegrationsSection from "./sections/IntegrationsSection";
import ModulesSection from "./sections/ModulesSection";
import TrustPageSection from "./sections/TrustPageSection";
import PartnersSection from "./sections/PartnersSection";
import MSPSettingsSection from "./sections/MSPSettingsSection";
import SecuritySection from "./sections/SecuritySection";
import MenuSettingsSection from "./sections/MenuSettingsSection";
import DiagnosticsSection from "./sections/DiagnosticsSection";
import CalendarSettingsSection from "./sections/CalendarSettingsSection";

const tabContent = {
  account: <AccountSection />,
  theme: <ThemeSettings />,
  security: <SecuritySection />,
  general: <GeneralSection />,
  billing: <BillingSection />,
  integrations: <IntegrationsSection />,
  modules: <ModulesSection />,
  features: <FeaturesSection />,
  trustPage: <TrustPageSection />,
  partners: <PartnersSection />,
  menus: <MenuSettingsSection />,
  calendar: <CalendarSettingsSection />,
  mspSettings: <div className="w-full"><MSPSettingsSection /></div>,
  diagnostics: <DiagnosticsSection />,
};

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState("account");
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Define required roles for each tab (empty array means no role required)
  const TAB_ROLES: Record<string, string[]> = {
    diagnostics: ['bugTester'],
    mspSettings: ['msp'],
  };

  const [userRoles, setUserRoles] = useState<Record<string, boolean>>({});
  const [rolesChecked, setRolesChecked] = useState(false);

  // Filter tabs based on user roles
  const filteredTabs = useMemo(() => {
    return SETTINGS_TABS.map(tab => {
      // If this is a group with options, filter the options
      if (tab.type === 'group' && tab.options) {
        const filteredOptions = tab.options.filter(option => {
          const requiredRoles = TAB_ROLES[option.key];
          
          if (!rolesChecked) {
            // Before roles are checked, hide tabs that require roles
            return !requiredRoles;
          }
          
          // After roles are checked, show tabs the user has access to
          return !requiredRoles || requiredRoles.some(role => userRoles[role]);
        });
        
        // Only include the group if it has visible options
        return filteredOptions.length > 0 ? { ...tab, options: filteredOptions } : null;
      }
      
      // For non-group tabs (backwards compatibility)
      const requiredRoles = TAB_ROLES[tab.key];
      if (!rolesChecked) {
        return !requiredRoles ? tab : null;
      }
      return !requiredRoles || requiredRoles.some(role => userRoles[role]) ? tab : null;
    }).filter(tab => tab !== null);
  }, [rolesChecked, userRoles]);

  // Function to handle tab changes
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    // Update URL when tab changes
    navigate(`/settings/${tabKey}`);
  };

  // Fetch user info to determine role-based tab visibility
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const info: any = await getUserInfo();
        if (mounted && info?.roles) {
          setUserRoles(info.roles);
        }
      } catch (e) {
        console.error('Error loading user info:', e);
      } finally {
        if (mounted) setRolesChecked(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Update active tab based on URL path
  useEffect(() => {
    if (!rolesChecked) return;
    
    const path = location.pathname;
    const section = path.split('/').pop();

    // If the section exists in our tabContent and user has required roles, set it as active
    if (section && section !== 'settings' && tabContent[section]) {
      const requiredRoles = TAB_ROLES[section];
      const hasAccess = !requiredRoles || requiredRoles.some(role => userRoles[role]);
      
      if (!hasAccess) {
        // Redirect to first available tab if user doesn't have access
        const firstAvailableTab = filteredTabs[0]?.key || 'account';
        setActiveTab(firstAvailableTab);
        navigate(`/settings/${firstAvailableTab}`);
        navigate('/settings/account', { replace: true });
      } else {
        setActiveTab(section);
      }
    } else if (path === '/settings') {
      // Default to account when just /settings is accessed
      setActiveTab('account');
    }
  }, [location.pathname, userRoles, rolesChecked, filteredTabs, navigate]);

  return (
    <div className="settings-page-wrapper flex w-full">
      {/* Settings sidebar */}
      <div className="hidden md:block w-80 border-r border-border bg-card flex-shrink-0">
        <div className="sticky top-0">
          <AppSettingsSidebar active={activeTab} onTabChange={handleTabChange} tabs={filteredTabs} />
        </div>
      </div>
      {/* Main settings content */}
      <div className="flex-1 bg-background min-w-0">
        <div className="settings-page-content w-full max-w-[100vw] mx-auto px-4 sm:px-6 mt-12 pb-24">
          <h1 className="text-3xl font-bold mb-2 text-foreground">Settings</h1>
          {/* Tabs as horizontal nav for mobile only, hidden on md+ */}
          <div className="flex flex-col gap-2 mb-8 mt-4 md:hidden w-full overflow-x-hidden">
            {filteredTabs.map((tab) => {
              if (tab.type === 'group' && tab.options) {
                return (
                  <div key={tab.label} className="space-y-2">
                    <h3 className="text-sm font-medium text-muted-foreground px-2">{tab.label}</h3>
                    <div className="flex gap-2 overflow-x-auto pb-2 w-full max-w-[calc(100vw-2rem)]">
                      {tab.options.map((option) => (
                        <button
                          key={option.key}
                          className={`flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-colors items-center gap-2 ${activeTab === option.key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
                          onClick={() => handleTabChange(option.key)}
                          type="button"
                        >
                          {option.icon && <FontAwesomeIcon icon={option.icon} className="h-4 w-4" />}
                          <span>{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }
              
              return (
                <button
                  key={tab.key}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === tab.key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
                  onClick={() => tab.key && handleTabChange(tab.key)}
                  type="button"
                >
                  {tab.icon && <FontAwesomeIcon icon={tab.icon} className="h-4 w-4" />}
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
          <div className="w-full h-full">{tabContent[activeTab]}</div>
        </div>
      </div>
    </div>
  );
}
